<template>
  <div class="formulario-modelo">
    <LoadingOverlay :visible="loading" message="Cargando evaluación..." />
    <MensajeToast ref="toastRef" />

    <div v-if="!loading" class="marco">
      <div class="encabezado-flex">
        <h2>Evaluación por Expertos</h2>
        <div class="acciones-encabezado">
          <button v-if="filas.length" class="btn toggle-panel" @click="mostrarPanel = !mostrarPanel">
            {{ mostrarPanel ? 'Cerrar' : 'Mostrar / ocultar' }}
          </button>
          <button class="btn salir" @click="volver">Volver</button>
        </div>
      </div>

      <div class="divisor"></div>

      <!-- Panel mostrar/ocultar primer nivel -->
      <div v-if="mostrarPanel && primerNivelItems.length" class="panel-ocultar">
        <p class="panel-titulo">Mostrar u ocultar {{ nombresNivelesComponentes[0] ?? 'Nivel 1' }}</p>
        <div class="panel-items">
          <label v-for="item in primerNivelItems" :key="item.key" class="chk">
            <input type="checkbox" :checked="!estaOculto(item.key)" @change="toggleOculto(item.key)" />
            {{ item.key }}. {{ item.nombre }}
          </label>
        </div>
      </div>

      <div v-if="!filas.length" class="sin-datos">
        No se encontraron componentes, indicadores o instrumentos para esta versión.
      </div>

      <div
        v-else
        ref="matrizWrap"
        class="matriz-wrap"
        @mousedown="iniciarArrastre"
        @mousemove="moverArrastre"
        @mouseup="detenerArrastre"
        @mouseleave="detenerArrastre"
      >
        <div class="matriz-excel">

          <!-- TÍTULO -->
          <div class="matriz-titulo">
            MATRIZ DE CALIFICACIÓN POR EXPERTOS
          </div>

          <!-- LEYENDA -->
          <div class="matriz-leyenda">
            <template v-if="esModeloHistorico">
              Modelo histórico · Calificaciones provenientes de la nota final registrada en la evaluación de cada componente.
            </template>
            <template v-else>
              Celdas amarillas = Documental (30%) · Celdas verdes = Cuantitativo (35%) · Celdas azules = Opinión (35%).
              Los valores provienen de la evaluación de indicadores. Los totales se calculan automáticamente.
            </template>
          </div>

          <!-- ═══ TABLA: MODELO COMPLETO (indicadores + instrumentos) ═══ -->
          <table v-if="!esModeloHistorico" class="matriz-tabla">
            <colgroup>
              <template v-for="(_, index) in nombresNivelesComponentes" :key="`col-group-${index}`">
                <col class="col-comp-num-col" />
                <col class="col-comp-col" />
                <col class="col-comp-peso-col" />
              </template>
              <col class="col-e-col" />
              <col class="col-f-col" />
              <col class="col-g-col" />
              <col class="col-h-col" />
              <col class="col-k-col" />
              <col class="col-l-col" />
              <col class="col-m-col" />
              <col class="col-n-col" />
              <col class="col-o-col" />
              <col class="col-p-col" />
            </colgroup>
            <thead>
              <tr class="fila-header">
                <template v-for="(nombreNivel, index) in nombresNivelesComponentes" :key="`nivel-header-${index}`">
                  <th class="cell col-comp-num">N°</th>
                  <th class="cell col-comp-header">{{ nombreNivel }}</th>
                  <th class="cell col-comp-peso">PESO</th>
                </template>
                <th class="cell col-e">INDICADOR</th>
                <th class="cell col-f">PESO</th>
                <th class="cell col-g">INSTRUMENTO / FUENTE</th>
                <th class="cell col-h">TIPO</th>
                <th class="cell col-k fuente-doc">CAL.<br>DOCUMENTAL<br>(30%)</th>
                <th class="cell col-l fuente-cuant">CAL.<br>CUANTITATIVA<br>(35%)</th>
                <th class="cell col-m fuente-opin">CAL.<br>OPINIÓN<br>(35%)</th>
                <th class="cell col-n">CAL.<br>PONDERADA</th>
                <th class="cell col-o">NIVEL DE<br>CUMPLIMIENTO</th>
                <th class="cell col-p">FORTALEZA</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, idx) in filasRenderizadas" :key="`row-${idx}`">
                <tr v-if="item.tipo === 'subtotal'" class="fila-subtotal">
                  <td class="cell subtotal-label" :colspan="nombresNivelesComponentes.length * 3 + 4">
                    Subtotal de {{ nombresNivelesComponentes[0] ?? 'Nivel 1' }}: {{ item.sub.nombrePrimerNivel }}
                  </td>
                  <td class="cell"></td>
                  <td class="cell"></td>
                  <td class="cell"></td>
                  <td class="cell subtotal-valor">{{ formatNum(item.sub.calPonderada ?? 0) }}</td>
                  <td class="cell"></td>
                  <td class="cell"></td>
                </tr>

                <tr v-else :class="claseFilaTipo(item.fila.tipo)">
                  <template v-for="(_, nivel) in nombresNivelesComponentes" :key="`comp-${idx}-${nivel}`">
                    <template v-if="item.spansNivel[nivel]?.render">
                      <td class="cell col-comp-num" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaNumeros[nivel] ?? '' }}</td>
                      <td class="cell col-comp" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaComponentes[nivel] ?? '' }}</td>
                      <td class="cell col-comp-peso" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaPesos[nivel] ?? '' }}</td>
                    </template>
                  </template>
                  <td class="cell col-e">{{ item.fila.nombreIndicador }}</td>
                  <td class="cell col-f">{{ item.fila.pesoIndicador }}</td>
                  <td class="cell col-g">{{ item.fila.nombreInstrumento }}</td>
                  <td class="cell col-h">{{ item.fila.tipoInstrumento }}</td>
                  <td class="cell col-k" :class="claseCeldaFuente(item.fila, 'documental')">
                    <span v-if="item.fila.calDocumental != null && esTipoFuente(item.fila, 'documental')" class="valor-calculado">
                      {{ formatNum(item.fila.calDocumental) }}
                    </span>
                  </td>
                  <td class="cell col-l" :class="claseCeldaFuente(item.fila, 'cuantitativa')">
                    <span v-if="item.fila.calCuantitativa != null && esTipoFuente(item.fila, 'cuantitativa')" class="valor-calculado">
                      {{ formatNum(item.fila.calCuantitativa) }}
                    </span>
                  </td>
                  <td class="cell col-m" :class="claseCeldaFuente(item.fila, 'opinion')">
                    <span v-if="item.fila.calOpinion != null && esTipoFuente(item.fila, 'opinion')" class="valor-calculado">
                      {{ formatNum(item.fila.calOpinion) }}
                    </span>
                  </td>
                  <td class="cell col-n">
                    <span v-if="item.fila.calPonderada != null" class="valor-calculado">
                      {{ formatNum(item.fila.calPonderada) }}
                    </span>
                  </td>
                  <td class="cell col-o">{{ item.fila.grado ?? '—' }}</td>
                  <td class="cell col-p">
                    <span v-if="item.fila.clasificacion !== 'no_aplica'" class="badge-clas" :class="'clas-' + item.fila.clasificacion">
                      {{ clasifLabel(item.fila.clasificacion) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </template>
              <tr class="fila-total">
                <td class="cell total-label" :colspan="nombresNivelesComponentes.length * 3 + 4">CALIFICACIÓN GENERAL</td>
                <td class="cell col-k">{{ formatNum(totalesGenerales.documental) }}</td>
                <td class="cell col-l">{{ formatNum(totalesGenerales.cuantitativa) }}</td>
                <td class="cell col-m">{{ formatNum(totalesGenerales.opinion) }}</td>
                <td class="cell col-n total-valor">{{ formatNum(totalesGenerales.ponderada) }}</td>
                <td class="cell"></td>
                <td class="cell"></td>
              </tr>
            </tbody>
          </table>

          <!-- ═══ TABLA: MODELO HISTÓRICO (solo componentes + calificación) ═══ -->
          <table v-else class="matriz-tabla">
            <colgroup>
              <template v-for="(_, index) in nombresNivelesComponentes" :key="`col-group-h-${index}`">
                <col class="col-comp-num-col" />
                <col class="col-comp-col" />
                <col class="col-comp-peso-col" />
              </template>
              <col class="col-n-col" />
              <col class="col-o-col" />
              <col class="col-p-col" />
            </colgroup>
            <thead>
              <tr class="fila-header">
                <template v-for="(nombreNivel, index) in nombresNivelesComponentes" :key="`nivel-header-h-${index}`">
                  <th class="cell col-comp-num">N°</th>
                  <th class="cell col-comp-header">{{ nombreNivel }}</th>
                  <th class="cell col-comp-peso">PESO</th>
                </template>
                <th class="cell col-n">CALIFICACIÓN</th>
                <th class="cell col-o">NIVEL DE<br>CUMPLIMIENTO</th>
                <th class="cell col-p">FORTALEZA</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, idx) in filasRenderizadas" :key="`row-h-${idx}`">
                <tr v-if="item.tipo === 'subtotal'" class="fila-subtotal">
                  <td class="cell subtotal-label" :colspan="nombresNivelesComponentes.length * 3">
                    Subtotal {{ nombresNivelesComponentes[0] ?? 'Factor' }}: {{ item.sub.nombrePrimerNivel }}
                  </td>
                  <td class="cell subtotal-valor">{{ formatNum(item.sub.calPonderada ?? 0) }}</td>
                  <td class="cell"></td>
                  <td class="cell"></td>
                </tr>
                <tr v-else :class="claseFilaTipo(item.fila.tipo)">
                  <template v-for="(_, nivel) in nombresNivelesComponentes" :key="`comp-h-${idx}-${nivel}`">
                    <template v-if="item.spansNivel[nivel]?.render">
                      <td class="cell col-comp-num" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaNumeros[nivel] ?? '' }}</td>
                      <td class="cell col-comp" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaComponentes[nivel] ?? '' }}</td>
                      <td class="cell col-comp-peso" :rowspan="item.spansNivel[nivel].span">{{ item.fila.rutaPesos[nivel] ?? '' }}</td>
                    </template>
                  </template>
                  <td class="cell col-n">
                    <span v-if="item.fila.calPonderada != null" class="valor-calculado">{{ formatNum(item.fila.calPonderada) }}</span>
                  </td>
                  <td class="cell col-o">{{ item.fila.grado ?? '—' }}</td>
                  <td class="cell col-p">
                    <span v-if="item.fila.clasificacion !== 'no_aplica'" class="badge-clas" :class="'clas-' + item.fila.clasificacion">
                      {{ clasifLabel(item.fila.clasificacion) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </template>
              <tr class="fila-total">
                <td class="cell total-label" :colspan="nombresNivelesComponentes.length * 3">CALIFICACIÓN GENERAL</td>
                <td class="cell col-n total-valor">{{ formatNum(totalesGenerales.ponderada) }}</td>
                <td class="cell"></td>
                <td class="cell"></td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Evaluación por pares externos.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/evaluacion-expertos` (EvaluacionExpertos) — permiso `expertos.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useModelo } from '@/store/GestionModelo'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { getEvaluacionesIndicadorService } from '@/services/EvaluacionIndicadorService'
import { getProcesosService } from '@/services/ProcesoAutoevaluacionService'
import type Componente from '@/interfaces/Componente'
import type Indicador from '@/interfaces/Indicador'
import type Instrumento from '@/interfaces/Instrumetos'
import type IndicadorInstrumento from '@/interfaces/IndicadorInstrumento'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeToast from '@/components/MensajeToast.vue'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { podarArbolInactivo } from '@/utils/estructuraActiva'

/* ─── Tipos internos ─── */
interface FilaMatriz {
  tipo: 'componente' | 'indicador' | 'instrumento'
  idComponente: number | null
  numComponente: string
  nombreComponente: string
  rutaNumeros: string[]
  rutaComponentes: string[]
  rutaPesos: string[]
  pesoComponente: string
  nivelComponente: number
  idIndicador: number | null
  numIndicador: string
  nombreIndicador: string
  pesoIndicador: string
  idInstrumento: number | null
  idTipoComponente: number | null
  nombreInstrumento: string
  tipoInstrumento: string
  tipoFuente: 'documental' | 'cuantitativa' | 'opinion' | null
  calDocumental: number | null
  calCuantitativa: number | null
  calOpinion: number | null
  calPonderada: number | null
  evaluacionId: number | null
  grado: string | null
  clasificacion: 'fuerte' | 'debil' | 'muy_debil' | 'no_aplica'
}

interface FilaSubtotal {
  tipo: 'subtotal'
  nombrePrimerNivel: string
  calPonderada: number | null
}

// Por cada nivel de componente, cuántas filas ocupa (rowspan) y si debe renderizarse en esta fila
interface SpanInfo {
  render: boolean   // false = esta celda está "fusionada" con la superior, no renderizar
  span: number      // cuántas filas abarca (para rowspan)
}

type FilaRenderizada =
  | { tipo: 'data'; fila: FilaMatriz; spansNivel: SpanInfo[] }
  | { tipo: 'subtotal'; sub: FilaSubtotal }

/* ─── State ─── */
const route = useRoute()
const router = useRouter()
const storeComponentes = useComponentes()
const storeIndicadores = useGestionIndicadores()
const storeRelaciones = useGestionIndicadorInstrumentos()
const storeInstrumentos = useGestionInstrumento()
const storeTiposComponente = useGestionTipoComponente()
const storeVersiones = useGestionVersiones()
const storeModelos = useModelo()
const toastRef = ref<InstanceType<typeof MensajeToast> | null>(null)

const idVersion = Number(route.params.id_version)
const loading = ref(true)
const filas = ref<FilaMatriz[]>([])
const esModeloHistorico = ref(false)
const nombresNivelesModelo = ref<string[]>([])
const escalaMax = ref<number>(5)
const mostrarPanel = ref(false)
const ocultos = ref<Set<string>>(new Set())

const nombresNivelesComponentes = computed(() => {
  const normalizados = nombresNivelesModelo.value
    .map(nombre => nombre.trim())
    .filter(nombre => nombre.length > 0)

  const cantidadColumnas = Math.max(
    normalizados.length,
    ...filas.value.map(fila => fila.rutaComponentes.length),
    1
  )

  return Array.from({ length: cantidadColumnas }, (_, index) => {
    return normalizados[index] ?? `Nivel ${index + 1}`
  })
})

const filasRenderizadas = computed<FilaRenderizada[]>(() => {
  const filasBase = filas.value.filter(f => {
    const k = f.rutaNumeros[0]
    return !k || !ocultos.value.has(k)
  })
  const numNiveles = nombresNivelesComponentes.value.length

  // Primero armar lista con subtotales intercalados (referencias directas a FilaMatriz)
  type EntradaConSub = { tipo: 'data'; fila: FilaMatriz } | { tipo: 'subtotal'; sub: FilaSubtotal }
  const conSubtotales: EntradaConSub[] = []

  for (let index = 0; index < filasBase.length; index++) {
    const filaActual = filasBase[index]
    conSubtotales.push({ tipo: 'data', fila: filaActual })

    const primerNivelActual = filaActual.rutaNumeros[0] ?? ''
    const siguienteFila = filasBase[index + 1]
    const primerNivelSiguiente = siguienteFila?.rutaNumeros[0] ?? ''

    if (primerNivelActual && primerNivelActual !== primerNivelSiguiente) {
      conSubtotales.push({
        tipo: 'subtotal',
        sub: {
          tipo: 'subtotal',
          nombrePrimerNivel: filaActual.rutaComponentes[0] ?? primerNivelActual,
          calPonderada: subtotalPorPrimerNivel.value[primerNivelActual]?.ponderada ?? null
        }
      })
    }
  }

  // Calcular spans para columnas de componente, preservando la referencia directa a cada fila
  const resultado: FilaRenderizada[] = []
  const spansAux: Array<SpanInfo[] | null> = [] 

  for (let i = 0; i < conSubtotales.length; i++) {
    const entrada = conSubtotales[i]

    if (entrada.tipo === 'subtotal') {
      resultado.push({ tipo: 'subtotal', sub: entrada.sub })
      spansAux.push(null)
      continue
    }

    const fila = entrada.fila
    const spansNivel: SpanInfo[] = []

    for (let nivel = 0; nivel < numNiveles; nivel++) {
      const valorActual = fila.rutaNumeros[nivel] ?? ''

      if (!valorActual) {
        spansNivel.push({ render: false, span: 0 })
        continue
      }

      // Buscar hacia atrás si ya existe un dueño del span para este valor/nivel
      let yaAbarcada = false
      for (let k = resultado.length - 1; k >= 0; k--) {
        const prevSpans = spansAux[k]
        if (prevSpans === null) break // subtotal rompe el span
        const prevFila = (conSubtotales[k] as { tipo: 'data'; fila: FilaMatriz }).fila
        const prevVal = prevFila.rutaNumeros[nivel] ?? ''
        if (prevVal !== valorActual) break
        if (prevSpans[nivel]?.render) { yaAbarcada = true; break }
        // prevSpans[nivel].render=false → sigue mirando atrás
      }

      if (yaAbarcada) {
        spansNivel.push({ render: false, span: 0 })
        continue
      }

      // Contar filas consecutivas hacia adelante con el mismo valor en este nivel
      let span = 1
      for (let j = i + 1; j < conSubtotales.length; j++) {
        const sig = conSubtotales[j]
        if (sig.tipo === 'subtotal') break
        if (sig.fila.rutaNumeros[nivel] === valorActual) span++
        else break
      }

      spansNivel.push({ render: true, span })
    }

    resultado.push({ tipo: 'data', fila, spansNivel })
    spansAux.push(spansNivel)
  }

  return resultado
})

/* ─── Helpers ─── */
function formatNum(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return ''
  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val)
}

function showToast(msg: string, tipo: 'exito' | 'error' = 'exito') {
  toastRef.value?.showToast(msg, tipo)
}

function normalizarTexto(valor: string | null | undefined): string {
  return (valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function resolverClasificacionPorNota(cal: number | null, escala: number): 'fuerte' | 'debil' | 'muy_debil' | 'no_aplica' {
  if (cal == null) return 'no_aplica'
  const pct = cal / escala
  if (pct >= 0.75) return 'fuerte'
  if (pct >= 0.50) return 'debil'
  return 'muy_debil'
}

const primerNivelItems = computed(() => {
  const seen = new Set<string>()
  const items: { key: string; nombre: string }[] = []
  for (const fila of filas.value) {
    const key = fila.rutaNumeros[0]
    if (key && !seen.has(key)) {
      seen.add(key)
      items.push({ key, nombre: fila.rutaComponentes[0] ?? key })
    }
  }
  return items
})

function estaOculto(key: string): boolean {
  return ocultos.value.has(key)
}

function toggleOculto(key: string): void {
  const s = new Set(ocultos.value)
  s.has(key) ? s.delete(key) : s.add(key)
  ocultos.value = s
}

function clasifLabel(c: string): string {
  return ({ fuerte: 'Fuerte', debil: 'Débil', muy_debil: 'Muy débil', no_aplica: 'No aplica' } as Record<string, string>)[c] ?? '—'
}

function resolverTipoFuente(idTipoComponente: number | null, tipoInstrumento: string): 'documental' | 'cuantitativa' | 'opinion' | null {
  const tipo = idTipoComponente != null
    ? storeTiposComponente.tipos.find(item => item.id_tipo_componente === idTipoComponente)?.nombre
    : null

  const texto = normalizarTexto(tipo || tipoInstrumento)

  if (texto.includes('documental') || texto.includes('documento')) return 'documental'
  if (texto.includes('cuantitativ') || texto.includes('estadistic')) return 'cuantitativa'
  if (texto.includes('opinion') || texto.includes('encuesta')) return 'opinion'
  return null
}

function esTipoFuente(fila: FilaMatriz, tipo: 'documental' | 'cuantitativa' | 'opinion'): boolean {
  return fila.tipoFuente === tipo
}

function claseCeldaFuente(fila: FilaMatriz, tipo: 'documental' | 'cuantitativa' | 'opinion'): string {
  if (!esTipoFuente(fila, tipo)) return 'fuente-inactiva'
  if (tipo === 'documental') return 'fuente-activa-doc'
  if (tipo === 'cuantitativa') return 'fuente-activa-cuant'
  return 'fuente-activa-opin'
}

const subtotalPorPrimerNivel = computed(() => {
  const acumulado: Record<string, { ponderada: number | null }> = {}

  const filasAgrupables = filas.value.filter(fila => {
    if (!fila.rutaNumeros[0]) return false
    if (esModeloHistorico.value) return fila.tipo === 'componente' && fila.calPonderada != null
    if (fila.tipo === 'instrumento') return fila.calPonderada != null
    if (fila.tipo === 'indicador') return fila.calPonderada != null
    return false
  })

  for (const fila of filasAgrupables) {
    const clave = fila.rutaNumeros[0]
    if (!acumulado[clave]) {
      acumulado[clave] = { ponderada: null }
    }
  }

  const claves = [...new Set(filasAgrupables.map(fila => fila.rutaNumeros[0]))]
  for (const clave of claves) {
    const filasDelGrupo = filasAgrupables.filter(fila => fila.rutaNumeros[0] === clave)
    const ponderadas = filasDelGrupo
      .map(fila => fila.calPonderada)
      .filter((valor): valor is number => valor != null && !isNaN(valor))

    acumulado[clave] = {
      ponderada: ponderadas.length
        ? ponderadas.reduce((suma, valor) => suma + valor, 0) / ponderadas.length
        : null
    }
  }

  return acumulado
})

function claseFilaTipo(tipo: string): string {
  if (tipo === 'componente') return 'fila-componente'
  if (tipo === 'indicador') return 'fila-indicador'
  return 'fila-instrumento'
}

function volver() {
  router.back()
}

/* ─── Arrastre de la tabla ─── */
const matrizWrap = ref<HTMLElement | null>(null)
let arrastrando = false
let arrastreX = 0
let arrastreY = 0
let scrollIniX = 0
let scrollIniY = 0

function iniciarArrastre(e: MouseEvent): void {
  const el = matrizWrap.value
  if (!el) return
  arrastrando = true
  arrastreX = e.clientX
  arrastreY = e.clientY
  scrollIniX = el.scrollLeft
  scrollIniY = el.scrollTop
  el.style.cursor = 'grabbing'
  el.style.userSelect = 'none'
}

function moverArrastre(e: MouseEvent): void {
  if (!arrastrando || !matrizWrap.value) return
  e.preventDefault()
  matrizWrap.value.scrollLeft = scrollIniX - (e.clientX - arrastreX)
  matrizWrap.value.scrollTop  = scrollIniY - (e.clientY - arrastreY)
}

function detenerArrastre(): void {
  arrastrando = false
  if (matrizWrap.value) {
    matrizWrap.value.style.cursor = 'grab'
    matrizWrap.value.style.userSelect = ''
  }
}

/* ─── Carga de datos ─── */
onMounted(async () => {
  try {
    loading.value = true

    if (!Number.isFinite(idVersion) || idVersion <= 0) {
      showToast('ID de versión inválido', 'error')
      return
    }

    await storeTiposComponente.getTipos()

    const version = await storeVersiones.getVersionById(idVersion)
    const idModelo = version?.id_modelo ?? null
    if (idModelo) {
      const modelo = await storeModelos.getModeloById(idModelo)
      nombresNivelesModelo.value = limpiarNiveles(modelo?.niveles)
      escalaMax.value = modelo?.escala_max ?? 5
    }

    const raiz = idModelo ? await storeComponentes.cargarRaizPorModelo(idModelo) : null
    if (!raiz?.id_componente) {
      showToast('No se encontró componente raíz para este modelo', 'error')
      return
    }
    await storeComponentes.cargarArbolDesdeRaiz(raiz.id_componente)

    await Promise.all([
      storeRelaciones.getRelaciones(),
      storeIndicadores.cargarTodos(),
      storeInstrumentos.cargarTodos()
    ])

    // Detectar si es modelo histórico (sin instrumentos vinculados)
    const indicadoresVersion = storeIndicadores.indicadores.filter(i => i.id_version === idVersion)
    const idsInd = new Set(indicadoresVersion.map(i => i.id_indicador).filter((id): id is number => id != null))
    esModeloHistorico.value = idsInd.size > 0 && !storeRelaciones.relaciones.some(r => idsInd.has(r.id_indicador))

    const todasEvaluaciones = await getEvaluacionesIndicadorService({ id_version: idVersion })
    const procesos = await getProcesosService({ id_version: idVersion })
    const proceso = procesos.find(p => p.estado !== 'finalizado') ?? procesos[procesos.length - 1] ?? null
    const idEjecucion = proceso?.id ?? null
    const evaluacionesExistentes = idEjecucion != null
      ? todasEvaluaciones.filter(e => e.id_ejecucion === idEjecucion)
      : todasEvaluaciones
    // Oculta aspectos/componentes sin indicador en esta versión (estructura compartida).
    const activos = await storeVersiones.getIdsComponentesActivos(idVersion)
    construirMatriz(podarArbolInactivo(storeComponentes.componentes, activos), evaluacionesExistentes, esModeloHistorico.value)

    recalcular()
  } catch (err) {
    console.error('Error al cargar evaluación:', err)
    showToast('Error al cargar los datos', 'error')
  } finally {
    loading.value = false
  }
})

/* ─── Construir matriz ─── */
function construirMatriz(
  arbol: Componente[],
  evaluaciones: any[],
  historicoModelo = false
) {
  const resultado: FilaMatriz[] = []

  function recorrer(
    componentes: Componente[],
    nivel: number,
    contadorComp: number[],
    rutaNumerosPadre: string[],
    rutaPesosPadre: string[],
    rutaPadre: string[]
  ) {
    const componentesOrdenados = [...componentes].sort((a, b) => {
      const ordA = a.ordinal ?? ''
      const ordB = b.ordinal ?? ''
      return ordA.localeCompare(ordB, undefined, { numeric: true, sensitivity: 'base' })
    })
    for (let i = 0; i < componentesOrdenados.length; i++) {
      const comp = componentesOrdenados[i]
      const numComp = [...contadorComp, i + 1].join('.')
      const esHoja = !comp.children?.length
      const rutaNumerosActual = [...rutaNumerosPadre, numComp]
      const rutaActual = [...rutaPadre, comp.nombre]
      const rutaPesosActual = [...rutaPesosPadre, comp.peso != null ? String(comp.peso) : '']

      // Obtener indicadores del componente hoja
      let indicadores: Indicador[] = []
      if (esHoja && comp.id_componente) {
        indicadores = storeIndicadores.indicadores.filter(
          i => i.id_componente === comp.id_componente && i.id_version === idVersion
        )
      }

      if (esHoja && indicadores.length > 0) {
        const esHistoricoIndicador = indicadores.every(
          ind => normalizarTexto(ind.nombre ?? ind.enunciado) === normalizarTexto(comp.nombre)
        )

        if (historicoModelo || esHistoricoIndicador) {
          const evalsHistorico = indicadores
            .map(ind => evaluaciones.find(e => e.id_indicador === ind.id_indicador))
            .filter(Boolean)
          const vals = evalsHistorico
            .map(e => e?.calificacion_final)
            .filter((v): v is number => v != null)
          resultado.push({
            tipo: 'componente',
            idComponente: comp.id_componente ?? null,
            numComponente: numComp,
            nombreComponente: comp.nombre,
            rutaNumeros: rutaNumerosActual,
            rutaComponentes: rutaActual,
            rutaPesos: rutaPesosActual,
            pesoComponente: comp.peso != null ? String(comp.peso) : '',
            nivelComponente: nivel,
            idIndicador: indicadores[0]?.id_indicador ?? null,
            numIndicador: '',
            nombreIndicador: '',
            pesoIndicador: '',
            idInstrumento: null,
            idTipoComponente: null,
            nombreInstrumento: '',
            tipoInstrumento: '',
            tipoFuente: null,
            calDocumental: evalsHistorico[0]?.calificacion_documental ?? null,
            calCuantitativa: evalsHistorico[0]?.calificacion_cuantitativa ?? null,
            calOpinion: evalsHistorico[0]?.calificacion_opinion ?? null,
            calPonderada: vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null,
            evaluacionId: evalsHistorico[0]?.id ?? null,
            grado: evalsHistorico[0]?.nivel_asignado ?? null,
            clasificacion: resolverClasificacionPorNota(vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null, escalaMax.value)
          })
        } else {

        for (let j = 0; j < indicadores.length; j++) {
          const ind = indicadores[j]
          // Buscar instrumentos vinculados a este indicador
          const relaciones = storeRelaciones.relaciones.filter(
            (r: IndicadorInstrumento) => r.id_indicador === ind.id_indicador
          )

          const instrumentos = relaciones
            .map(rel => storeInstrumentos.instrumentos.find(i => i.id_instrumento === rel.id_instrumento))
            .filter((i): i is Instrumento => i != null)

          // Buscar evaluación existente
          const evalExistente = evaluaciones.find(
            e => e.id_indicador === ind.id_indicador
          )

          if (instrumentos.length > 0) {
            for (const inst of instrumentos) {
              resultado.push({
                tipo: 'instrumento',
                idComponente: comp.id_componente ?? null,
                numComponente: numComp,
                nombreComponente: comp.nombre,
                rutaNumeros: rutaNumerosActual,
                rutaComponentes: rutaActual,
                rutaPesos: rutaPesosActual,
                pesoComponente: comp.peso != null ? String(comp.peso) : '',
                nivelComponente: nivel,
                idIndicador: ind.id_indicador,
                numIndicador: String(j + 1),
                nombreIndicador: ind.nombre ?? ind.enunciado,
                pesoIndicador: ind.peso_snapshot != null ? String(ind.peso_snapshot) : '',
                idInstrumento: inst.id_instrumento ?? null,
                idTipoComponente: inst.id_tipo_componente ?? ind.id_tipo_componente ?? null,
                nombreInstrumento: inst.herramienta,
                tipoInstrumento: inst.descripcion ?? '',
                tipoFuente: resolverTipoFuente(inst.id_tipo_componente ?? ind.id_tipo_componente ?? null, inst.descripcion ?? ''),
                calDocumental: evalExistente?.calificacion_documental ?? null,
                calCuantitativa: evalExistente?.calificacion_cuantitativa ?? null,
                calOpinion: evalExistente?.calificacion_opinion ?? null,
                calPonderada: evalExistente?.calificacion_final ?? null,
                evaluacionId: evalExistente?.id ?? null,
                grado: evalExistente?.nivel_asignado ?? null,
                clasificacion: resolverClasificacionPorNota(evalExistente?.calificacion_final ?? null, escalaMax.value)
              })
            }
          } else {
            // Indicador sin instrumentos: fila de indicador solo
            resultado.push({
              tipo: 'indicador',
              idComponente: comp.id_componente ?? null,
              numComponente: numComp,
              nombreComponente: comp.nombre,
              rutaNumeros: rutaNumerosActual,
              rutaComponentes: rutaActual,
              rutaPesos: rutaPesosActual,
              pesoComponente: comp.peso != null ? String(comp.peso) : '',
              nivelComponente: nivel,
              idIndicador: ind.id_indicador,
              numIndicador: String(j + 1),
              nombreIndicador: ind.nombre ?? ind.enunciado,
              pesoIndicador: ind.peso_snapshot != null ? String(ind.peso_snapshot) : '',
              idInstrumento: null,
              idTipoComponente: ind.id_tipo_componente ?? null,
              nombreInstrumento: '',
              tipoInstrumento: '',
              tipoFuente: resolverTipoFuente(ind.id_tipo_componente ?? null, ''),
              calDocumental: evalExistente?.calificacion_documental ?? null,
              calCuantitativa: evalExistente?.calificacion_cuantitativa ?? null,
              calOpinion: evalExistente?.calificacion_opinion ?? null,
              calPonderada: evalExistente?.calificacion_final ?? null,
              evaluacionId: evalExistente?.id ?? null,
              grado: evalExistente?.nivel_asignado ?? null,
              clasificacion: resolverClasificacionPorNota(evalExistente?.calificacion_final ?? null, escalaMax.value)
            })
          }
        }
        } // end else (no histórico)
      } else if (!esHoja) {
        // Los componentes padre solo estructuran la ruta; no se muestran como filas independientes.
        if (comp.children?.length) {
          recorrer(comp.children, nivel + 1, [...contadorComp, i + 1], rutaNumerosActual, rutaPesosActual, rutaActual)
        }
      } else {
        resultado.push({
          tipo: 'componente',
          idComponente: comp.id_componente ?? null,
          numComponente: numComp,
          nombreComponente: comp.nombre,
          rutaNumeros: rutaNumerosActual,
          rutaComponentes: rutaActual,
          rutaPesos: rutaPesosActual,
          pesoComponente: comp.peso != null ? String(comp.peso) : '',
          nivelComponente: nivel,
          idIndicador: null,
          numIndicador: '',
          nombreIndicador: '',
          pesoIndicador: '',
          idInstrumento: null,
          idTipoComponente: null,
          nombreInstrumento: '',
          tipoInstrumento: '',
          tipoFuente: null,
          calDocumental: null,
          calCuantitativa: null,
          calOpinion: null,
          calPonderada: null,
          evaluacionId: null,
          grado: null,
          clasificacion: 'no_aplica' as const
        })
      }
    }
  }

  // Empezar desde los hijos de la raíz (la raíz es invisible)
  const raiz = arbol[0]
  if (raiz?.children?.length) {
    recorrer(raiz.children, 0, [], [], [], [])
  }

  filas.value = resultado
}

/* ─── Recalcular ponderaciones (instrumentos → indicadores → componentes) ─── */
function recalcular() {
  const rows = filas.value

  // 1. Calcular ponderada de cada instrumento
  for (const fila of rows) {
    if (fila.tipo === 'instrumento') {
      const d = fila.calDocumental
      const c = fila.calCuantitativa
      const o = fila.calOpinion

      const partes = [
        { val: d, peso: 0.30 },
        { val: c, peso: 0.35 },
        { val: o, peso: 0.35 }
      ].filter(p => p.val != null && !isNaN(p.val!))

      if (partes.length > 0) {
        const totalPeso = partes.reduce((s, p) => s + p.peso, 0)
        const totalVal = partes.reduce((s, p) => s + (p.val! * p.peso), 0)
        fila.calPonderada = totalVal / totalPeso
      } else {
        fila.calPonderada = null
      }
      fila.clasificacion = resolverClasificacionPorNota(fila.calPonderada, escalaMax.value)
    }
  }

  // 2. Agrupar instrumentos por indicador y calcular promedio → indicador
  const indicadorIds = [...new Set(
    rows.filter(r => r.tipo === 'instrumento' && r.idIndicador != null).map(r => r.idIndicador!)
  )]

  const promedioIndicador: Record<number, { doc: number | null; cuant: number | null; opin: number | null; pond: number | null }> = {}

  for (const idInd of indicadorIds) {
    const instrumentos = rows.filter(r => r.tipo === 'instrumento' && r.idIndicador === idInd)
    const conDoc = instrumentos.filter(r => r.calDocumental != null)
    const conCuant = instrumentos.filter(r => r.calCuantitativa != null)
    const conOpin = instrumentos.filter(r => r.calOpinion != null)
    const conPond = instrumentos.filter(r => r.calPonderada != null)

    promedioIndicador[idInd] = {
      doc: conDoc.length ? conDoc.reduce((s, r) => s + r.calDocumental!, 0) / conDoc.length : null,
      cuant: conCuant.length ? conCuant.reduce((s, r) => s + r.calCuantitativa!, 0) / conCuant.length : null,
      opin: conOpin.length ? conOpin.reduce((s, r) => s + r.calOpinion!, 0) / conOpin.length : null,
      pond: conPond.length ? conPond.reduce((s, r) => s + r.calPonderada!, 0) / conPond.length : null
    }
  }

  // 3. Agrupar indicadores por componente y calcular promedio ponderado → componente
  const componenteIds = [...new Set(
    rows.filter(r => r.tipo === 'componente' && r.idComponente != null).map(r => r.idComponente!)
  )]

  for (const idComp of componenteIds) {
    // Buscar todos los indicadores que pertenecen a los hijos de este componente
    const indicadoresDeComp = rows.filter(
      r => (r.tipo === 'instrumento' || r.tipo === 'indicador') && r.idComponente === idComp
    )

    const idsIndUnicos = [...new Set(indicadoresDeComp.filter(r => r.idIndicador != null).map(r => r.idIndicador!))]

    if (idsIndUnicos.length === 0) continue

    let sumaDoc = 0, countDoc = 0
    let sumaCuant = 0, countCuant = 0
    let sumaOpin = 0, countOpin = 0
    let sumaPond = 0, countPond = 0

    for (const idInd of idsIndUnicos) {
      const prom = promedioIndicador[idInd]
      if (prom) {
        if (prom.doc != null) { sumaDoc += prom.doc; countDoc++ }
        if (prom.cuant != null) { sumaCuant += prom.cuant; countCuant++ }
        if (prom.opin != null) { sumaOpin += prom.opin; countOpin++ }
        if (prom.pond != null) { sumaPond += prom.pond; countPond++ }
      }
    }

    // Actualizar la fila del componente
    const filaComp = rows.find(r => r.tipo === 'componente' && r.idComponente === idComp)
    if (filaComp) {
      filaComp.calDocumental = countDoc > 0 ? sumaDoc / countDoc : null
      filaComp.calCuantitativa = countCuant > 0 ? sumaCuant / countCuant : null
      filaComp.calOpinion = countOpin > 0 ? sumaOpin / countOpin : null
      filaComp.calPonderada = countPond > 0 ? sumaPond / countPond : null
      filaComp.clasificacion = resolverClasificacionPorNota(filaComp.calPonderada, escalaMax.value)
    }
  }
}

/* ─── Totales generales ─── */
const totalesGenerales = computed(() => {
  if (esModeloHistorico.value) {
    const comps = filas.value.filter(r => r.tipo === 'componente')
    const conPond = comps.filter(r => r.calPonderada != null)
    return {
      documental: null,
      cuantitativa: null,
      opinion: null,
      ponderada: conPond.length ? conPond.reduce((s, r) => s + r.calPonderada!, 0) / conPond.length : null
    }
  }

  const comps = filas.value.filter(r => r.tipo === 'componente' && r.nivelComponente === 0)

  if (!comps.length) {
    // Si no hay componentes de nivel 0, usar todos los instrumentos
    const instrumentos = filas.value.filter(r => r.tipo === 'instrumento')
    const conPond = instrumentos.filter(r => r.calPonderada != null)
    const conDoc = instrumentos.filter(r => r.calDocumental != null)
    const conCuant = instrumentos.filter(r => r.calCuantitativa != null)
    const conOpin = instrumentos.filter(r => r.calOpinion != null)

    return {
      documental: conDoc.length ? conDoc.reduce((s, r) => s + r.calDocumental!, 0) / conDoc.length : null,
      cuantitativa: conCuant.length ? conCuant.reduce((s, r) => s + r.calCuantitativa!, 0) / conCuant.length : null,
      opinion: conOpin.length ? conOpin.reduce((s, r) => s + r.calOpinion!, 0) / conOpin.length : null,
      ponderada: conPond.length ? conPond.reduce((s, r) => s + r.calPonderada!, 0) / conPond.length : null
    }
  }

  const conDoc = comps.filter(r => r.calDocumental != null)
  const conCuant = comps.filter(r => r.calCuantitativa != null)
  const conOpin = comps.filter(r => r.calOpinion != null)
  const conPond = comps.filter(r => r.calPonderada != null)

  return {
    documental: conDoc.length ? conDoc.reduce((s, r) => s + r.calDocumental!, 0) / conDoc.length : null,
    cuantitativa: conCuant.length ? conCuant.reduce((s, r) => s + r.calCuantitativa!, 0) / conCuant.length : null,
    opinion: conOpin.length ? conOpin.reduce((s, r) => s + r.calOpinion!, 0) / conOpin.length : null,
    ponderada: conPond.length ? conPond.reduce((s, r) => s + r.calPonderada!, 0) / conPond.length : null
  }
})

</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

.marco {
  border: 2px solid #8f8f8f;
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.encabezado-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.acciones-encabezado {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.divisor {
  border-bottom: 1px solid #949393;
  margin: 12px 0 16px;
}

.sin-datos {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 15px;
}

/* ─── Botones ─── */
.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
  min-width: 120px;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.btn.toggle-panel {
  background-color: white;
  border: 1px solid #0e69a0;
  color: #0e69a0;
}

.btn.toggle-panel:hover {
  background-color: #0e69a0;
  color: white;
}

.panel-ocultar {
  background: #f4f8fc;
  border: 1px solid #c0d8ec;
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 4px;
}

.panel-titulo {
  font-weight: 700;
  color: #0e69a0;
  margin: 0 0 10px;
  font-size: 13px;
}

.panel-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
}

.chk {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
}

.chk input[type="checkbox"] {
  cursor: pointer;
  accent-color: #0e69a0;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

/* ─── Contenedor Excel ─── */
.matriz-wrap {
  overflow: auto;
  border: 1px solid #c9c9c9;
  background: #fff;
  cursor: grab;
}

.matriz-excel {
  min-width: fit-content;
  font-size: 13px;
  line-height: 1.15;
}

/* ─── Título y Leyenda ─── */
.matriz-titulo {
  background: #0d3b66;
  color: white;
  font-weight: 700;
  text-align: center;
  padding: 12px 10px;
  border-bottom: 1px solid #ffffff;
}

.matriz-leyenda {
  background: #fffde7;
  color: #333;
  text-align: center;
  padding: 10px;
  border-bottom: 1px solid #cfcfcf;
  font-size: 12px;
}

/* ─── Tabla principal ─── */
.matriz-tabla {
  border-collapse: collapse;
  width: 100%;
  min-width: max-content;
  font-size: 13px;
  line-height: 1.15;
}

.col-comp-num-col { width: 56px; }
.col-comp-col     { width: 220px; }
.col-comp-peso-col{ width: 65px; }
.col-e-col        { width: 240px; }
.col-f-col        { width: 65px; }
.col-g-col        { width: 240px; }
.col-h-col        { width: 110px; }
.col-k-col        { width: 100px; }
.col-l-col        { width: 100px; }
.col-m-col        { width: 100px; }
.col-n-col        { width: 100px; }
.col-o-col        { width: 130px; }
.col-p-col        { width: 110px; }

/* ─── Celdas ─── */
.cell {
  border: 1px solid #a9a9a9;
  padding: 6px 8px;
  min-height: 38px;
  box-sizing: border-box;
  background: white;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 12px;
  vertical-align: middle;
}

/* ─── Cabecera ─── */
.fila-header th.cell {
  background: #1f5f8b;
  color: white;
  font-weight: 700;
  text-align: center;
  font-size: 11px;
}

.fila-header th.cell.fuente-doc { background: #856404; }
.fila-header th.cell.fuente-cuant { background: #1a6b3c; }
.fila-header th.cell.fuente-opin { background: #0c5460; }

/* ─── Colores por columna ─── */
.col-comp-num,
.col-comp-peso {
  font-weight: 600;
  text-align: center;
}

.col-comp,
.col-comp-header {
  text-align: left;
}

.col-k,
.col-l,
.col-m {
  background: #ffffff;
}

.col-n {
  background: #ffffff;
  font-weight: 700;
  text-align: center;
}

/* ─── Filas según tipo ─── */
tr.fila-componente td.cell {
  background: #eef5fb;
  color: #0e69a0;
  font-weight: 700;
}

tr.fila-componente td.col-k,
tr.fila-componente td.col-l,
tr.fila-componente td.col-m,
tr.fila-componente td.col-n {
  background: #ffffff;
  color: #111;
  text-align: center;
}

tr.fila-indicador td.cell {
  background: #d6e8f5;
  color: #111;
  font-weight: 600;
}

tr.fila-indicador td.col-k,
tr.fila-indicador td.col-l,
tr.fila-indicador td.col-m {
  background: #ffffff;
}
tr.fila-indicador td.col-n { background: #ffffff; }

tr.fila-instrumento td.cell {
  background: #f8f9fa;
}

tr.fila-instrumento td.col-k,
tr.fila-instrumento td.col-l,
tr.fila-instrumento td.col-m {
  background: #ffffff;
}
tr.fila-instrumento td.col-n { background: #ffffff; }

.fuente-inactiva {
  background: #ffffff !important;
}

.fuente-activa-doc {
  background: #fff3cd !important;
}

.fuente-activa-cuant {
  background: #d4edda !important;
}

.fuente-activa-opin {
  background: #d1ecf1 !important;
}

/* ─── Fila total ─── */
tr.fila-total td.cell {
  background: #0d3b66;
  color: white;
  font-weight: 700;
  text-align: center;
}

tr.fila-subtotal td.cell {
  background: #083b66;
  color: #fff;
  font-weight: 700;
  text-align: center;
}

.subtotal-label {
  text-align: center !important;
}

.subtotal-valor {
  font-size: 14px;
  background: #ffffff !important;
  color: #111 !important;
}

.total-label {
  font-size: 13px;
  text-align: left !important;
}

.total-valor {
  font-size: 14px;
}

/* ─── Inputs ─── */
.input-matriz {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  padding: 4px 2px;
  border-radius: 3px;
}

.input-doc { background: rgba(255, 243, 205, 0.5); }
.input-cuant { background: rgba(212, 237, 218, 0.5); }
.input-opin { background: rgba(209, 236, 241, 0.5); }

.input-matriz:focus {
  outline: 2px solid #0e69a0;
  background: white;
}

/* ─── Valores calculados ─── */
.valor-calculado {
  display: block;
  text-align: center;
  width: 100%;
  font-weight: 600;
  font-size: 12px;
}

.valor-ponderado {
  display: block;
  text-align: center;
  width: 100%;
  font-weight: 700;
  font-size: 13px;
}

/* ─── Badges de clasificación ─── */
.badge-clas { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }
.clas-fuerte   { background: #d4edda; color: #155724; }
.clas-debil    { background: #fff3cd; color: #856404; }
.clas-muy_debil { background: #f8d7da; color: #721c24; }
.col-o, .col-p { text-align: center; }

/* ─── Responsivo ─── */
@media (max-width: 1024px) {
  .formulario-modelo {
    min-width: unset;
    padding: 1rem;
  }

  .matriz-excel {
    min-width: max-content;
  }
}
</style>
